import * as React from "react";
import { OutlinedInput } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { sentry } from "@talentpair/sentry";
import { Box, Button } from "@talentpair/quantic";

export type MessageInputProps = {
  disabled?: boolean;
  onSubmit: (message: string) => Promise<unknown>;
  maxLength?: number;
};

export function MessageInput({
  disabled = false,
  onSubmit,
  maxLength = 5000,
}: MessageInputProps): React.ReactElement {
  const messageRef = React.useRef<HTMLTextAreaElement>(null);

  const submitMessage = (): void => {
    const el = messageRef.current;
    if (!el || disabled) return;
    let { value } = el;
    value = value.trim();
    el.value = "";

    onSubmit(value).catch((err) => {
      // reset message back into message box on error
      el.value = value;
      sentry.error(err);
    });
  };
  // list of the modifier keys we watch for so we don't submit if any of these are pressed
  const modifierKeys = ["metaKey", "ctrlKey", "altKey", "shiftKey"];

  return (
    <OutlinedInput
      name="message"
      autoFocus
      fullWidth
      multiline
      maxRows={10}
      inputRef={messageRef}
      classes={{
        root: "border-none p2 flex-column items-stretch br2 border-grey-300",
        input: "px2",
      }}
      onClick={(e) => {
        if (
          (e.target as HTMLDivElement)?.dataset?.testid === "message-input-tray" &&
          messageRef.current
        )
          messageRef.current.focus();
      }}
      inputProps={{
        onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
          if (e.key !== "Enter") {
            const { value } = e.currentTarget;
            if (value.length > maxLength) e.currentTarget.value = value.slice(0, maxLength);
            return;
          }
          // ignore Enter key if a modifier key is also being pressed
          // @ts-expect-error - keyboard event types are slightly different, but not where it matters
          if (modifierKeys.some((k) => e[k])) return;
          e.preventDefault();
          const trimmed = e.currentTarget.value.trim();
          e.currentTarget.value = trimmed;
          submitMessage();
        },
        onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          // Don't trim trailing whitespace so that user can input separate paragraphs as they choose
          const trimmed = e.currentTarget.value.trimStart();
          if (trimmed === e.currentTarget.value) return;
          e.currentTarget.value =
            trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
        },
      }}
      placeholder="Message Perry"
      endAdornment={
        <Box
          flex="auto"
          variant="row"
          layout="center-end"
          className="mt2"
          data-testid="message-input-tray"
        >
          <Button
            variant="icon"
            icon={SendIcon}
            disabled={disabled}
            size="lg"
            background="#eee"
            onClick={submitMessage}
          />
        </Box>
      }
    />
  );
}
